"use client";

import { useEffect, useRef, useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";

type Phase = "work" | "short" | "long";

/** English strings. A translated content file overrides these through `ui`. */
export const POMODORO_TIMER_STRINGS = {
  phaseLabels: {
    work: "Focus",
    short: "Short Break",
    long: "Long Break",
  },
  tabTitleSuffix: " — Pomodoro Timer",
  sessionLabel: "Session ",
  longBreakEverySessions:
    "one: · long break every {count} session|other: · long break every {count} sessions",
  start: "Start",
  pause: "Pause",
  reset: "Reset",
  settingsMinutes: "Settings (minutes)",
  workSession: "Work session",
  shortBreak: "Short break",
  longBreak: "Long break",
  longBreakEveryLabel: "Long break every (sessions)",
  sessionsCompleted: "Sessions completed",
  currentPhase: "Current phase",
  sessionsUntilLongBreak: "Sessions until long break",
} as const;

function playBeep() {
  const ctx = new AudioContext();
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.type = "sine";
  oscillator.frequency.value = 880;
  gain.gain.setValueAtTime(0.0001, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
  oscillator.connect(gain);
  gain.connect(ctx.destination);
  oscillator.start();
  oscillator.stop(ctx.currentTime + 0.6);
  oscillator.onended = () => {
    ctx.close();
  };
}

function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function pomodoroDocumentTitle(
  secondsLeft: number,
  phaseLabel: string,
  suffix: string
): string {
  return `${formatTime(secondsLeft)} · ${phaseLabel}${suffix}`;
}

function clampMinutes(raw: string): number {
  const parsed = parseIntegerInput(raw);
  if (parsed === null) {
    return 1;
  }
  return Math.min(180, Math.max(1, parsed));
}

function SettingInput({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label
      className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm"
      htmlFor={id}
    >
      {label}
      <input
        className="w-16 rounded border border-border bg-background px-2 py-1 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        id={id}
        max={180}
        min={1}
        onChange={(e) => onChange(clampMinutes(e.target.value))}
        type="number"
        value={value}
      />
    </label>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

export default function PomodoroTimerWidget() {
  const locale = useToolLocale();
  const strings = useToolUi(POMODORO_TIMER_STRINGS);
  const [workMin, setWorkMin] = useState(25);
  const [shortMin, setShortMin] = useState(5);
  const [longMin, setLongMin] = useState(15);
  const [longEvery, setLongEvery] = useState(4);
  const [phase, setPhase] = useState<Phase>("work");
  const [secondsLeft, setSecondsLeft] = useState(25 * 60);
  const [running, setRunning] = useState(false);
  const [sessions, setSessions] = useState(0);
  const endAtRef = useRef<number | null>(null);
  const advanceRef = useRef<() => void>(() => undefined);
  const originalTitleRef = useRef<string | null>(null);

  function phaseDurationSeconds(target: Phase): number {
    if (target === "work") {
      return workMin * 60;
    }
    if (target === "short") {
      return shortMin * 60;
    }
    return longMin * 60;
  }

  function advancePhase() {
    let next: Phase = "work";
    let newSessions = sessions;
    if (phase === "work") {
      newSessions = sessions + 1;
      next = newSessions % longEvery === 0 ? "long" : "short";
    }
    const duration = phaseDurationSeconds(next);
    setSessions(newSessions);
    setPhase(next);
    setSecondsLeft(duration);
    endAtRef.current = Date.now() + duration * 1000;
  }
  advanceRef.current = advancePhase;

  useEffect(() => {
    if (!running) {
      return;
    }
    const id = setInterval(() => {
      const endAt = endAtRef.current;
      if (endAt === null) {
        return;
      }
      const remaining = Math.max(0, Math.round((endAt - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0) {
        playBeep();
        advanceRef.current();
      }
    }, 250);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    originalTitleRef.current = document.title;
    return () => {
      if (originalTitleRef.current !== null) {
        document.title = originalTitleRef.current;
      }
    };
  }, []);

  useEffect(() => {
    document.title = pomodoroDocumentTitle(
      secondsLeft,
      strings.phaseLabels[phase],
      strings.tabTitleSuffix
    );
  }, [secondsLeft, phase, strings.phaseLabels, strings.tabTitleSuffix]);

  function handleStartPause() {
    if (running) {
      setRunning(false);
      endAtRef.current = null;
      return;
    }
    endAtRef.current = Date.now() + secondsLeft * 1000;
    setRunning(true);
  }

  function handleReset() {
    setRunning(false);
    endAtRef.current = null;
    setPhase("work");
    setSessions(0);
    setSecondsLeft(workMin * 60);
  }

  function handleWorkMinChange(value: number) {
    setWorkMin(value);
    if (!running && phase === "work") {
      setSecondsLeft(value * 60);
    }
  }

  function handleShortMinChange(value: number) {
    setShortMin(value);
    if (!running && phase === "short") {
      setSecondsLeft(value * 60);
    }
  }

  function handleLongMinChange(value: number) {
    setLongMin(value);
    if (!running && phase === "long") {
      setSecondsLeft(value * 60);
    }
  }

  const untilLongBreak = longEvery - (sessions % longEvery);
  const phaseAccent = phase === "work" ? "text-foreground" : "text-primary";

  return (
    <>
      {/* Timer display */}
      <section className="rounded-lg border border-border bg-card p-8 text-center">
        <p
          className={`font-medium text-sm uppercase tracking-wide ${phaseAccent}`}
        >
          {strings.phaseLabels[phase]}
        </p>
        <p className="mt-2 font-mono font-semibold text-7xl text-foreground tabular-nums md:text-8xl">
          {formatTime(secondsLeft)}
        </p>
        <p className="mt-3 text-muted-foreground text-sm">
          {strings.sessionLabel}
          {sessions + (phase === "work" ? 1 : 0)}
          {tPlural(strings.longBreakEverySessions, locale, longEvery)}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button
            className="rounded-lg bg-primary px-6 py-2.5 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
            onClick={handleStartPause}
            type="button"
          >
            {running ? strings.pause : strings.start}
          </button>
          <button
            className="rounded-lg border border-border bg-card px-6 py-2.5 font-medium text-foreground text-sm transition-colors hover:bg-muted"
            onClick={handleReset}
            type="button"
          >
            {strings.reset}
          </button>
        </div>
      </section>

      {/* Settings */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.settingsMinutes}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <SettingInput
            id="work-min"
            label={strings.workSession}
            onChange={handleWorkMinChange}
            value={workMin}
          />
          <SettingInput
            id="short-min"
            label={strings.shortBreak}
            onChange={handleShortMinChange}
            value={shortMin}
          />
          <SettingInput
            id="long-min"
            label={strings.longBreak}
            onChange={handleLongMinChange}
            value={longMin}
          />
          <SettingInput
            id="long-every"
            label={strings.longBreakEveryLabel}
            onChange={setLongEvery}
            value={longEvery}
          />
        </div>
      </section>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard label={strings.sessionsCompleted} value={sessions} />
        <StatCard
          label={strings.currentPhase}
          value={strings.phaseLabels[phase]}
        />
        <StatCard
          label={strings.sessionsUntilLongBreak}
          value={untilLongBreak}
        />
      </div>
    </>
  );
}
